import React, { memo, useCallback, useState } from 'react';
import {
    PayStateT,
    MonthNumber,
} from 'routes/HumanResourcesModule/PayState/helpers/types';
import { useTranslation } from 'react-i18next';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import { useGetPayComponentSalaryMassData } from '../helpers/hooks/payStateData';
import MonthSelector from '../components/MonthSelector';
import {
    extractPivotedSuffixes,
    formatTableIndicators,
    shouldAddOtherPayComponent,
} from './helpers/dataHelpers';
import { getColDefs } from './helpers/colDefinitions';
import ShowLastYearToggle from '../components/ShowLastYearToggle';
import { getCurrentMonthForExercice } from 'routes/HumanResourcesModule/PayState/helpers/dataHelpers';
import { useGetTablePayCompSalaryMassIndicators } from '../helpers/hooks/payStateIndicators';
import Loading from 'components/Loading';
import PayStateBanner from '../components/PayStateBanner';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';

type Props = {
    payState: PayStateT;
    permanent?: boolean;
};

const PayComponentSalaryMassGrid = ({
    payState,
    permanent = false,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { id: payStateId, exercice } = payState;

    const [selectedMonth, setSelectedMonth] = useState<MonthNumber>(() =>
        getCurrentMonthForExercice(payState.exercice),
    );
    const [showLastYearColumns, setShowLastYearColumns] = useState(false);

    const {
        data: salaryMassByPayComponentResponse,
        isLoading: isSalaryMassResponseLoading,
    } = useGetPayComponentSalaryMassData(payStateId, selectedMonth, permanent);

    const { data: tableIndicators, isLoading: isTableIndicatorsLoading } =
        useGetTablePayCompSalaryMassIndicators(payStateId, permanent);

    const fmtIndicator = formatTableIndicators(tableIndicators);

    const salaryMassByPayComponent =
        salaryMassByPayComponentResponse?.data || [];

    const payComponents = extractPivotedSuffixes(
        salaryMassByPayComponent,
    ) as PayComponent[];

    // if BRUT pay component is displayed, we can add the OTHER pay component.
    const showOtherPayComponentColumn =
        shouldAddOtherPayComponent(payComponents);
    const columnDefs = getColDefs({
        payComponents,
        exercice,
        month: selectedMonth,
        showLastYearColumns,
        showOtherPayComponentColumn,
    });
    const exportFileName = t(
        'humanResources.payState.salaryMassByPayComponentExportFileName',
        { month: selectedMonth.toString() },
    );
    const toggleLastYearColumns = useCallback(
        () => setShowLastYearColumns((current) => !current),
        [],
    );
    const cardHeaderActions = (
        <>
            <ShowLastYearToggle
                checked={showLastYearColumns}
                onChange={toggleLastYearColumns}
                lastYear={exercice - 1}
            />
            <MonthSelector
                allYearOption
                exercice={payState.exercice}
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
            />
        </>
    );
    const bannerComponent = ({ children }: { children: React.ReactElement }) =>
        isTableIndicatorsLoading ? (
            <Loading />
        ) : (
            <PayStateBanner
                title={t(`humanResources.payState.indicators.cdp`)}
                exercice={payState.exercice}
                data={fmtIndicator}
            >
                {children}
            </PayStateBanner>
        );
    return (
        <StyHrDataGrid
            title={t(
                `humanResources.${permanent ? 'permanent' : 'nonPermanent'}SalaryMassByPayComponents`,
            )}
            infoTooltip={t(
                `humanResources.${permanent ? 'permanent' : 'nonPermanent'}SalaryMassByPayComponentsTooltip`,
            )}
            rowData={salaryMassByPayComponent}
            trackEventMetadata={{
                gridName: permanent
                    ? TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                          .payComponentPermanentGrid
                    : TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                          .payComponentNonPermanentGrid,
            }}
            columnDefs={columnDefs}
            exportFileName={exportFileName}
            isLoading={isSalaryMassResponseLoading}
            cardHeaderActions={cardHeaderActions}
            wrapperComponent={bannerComponent}
        />
    );
};

export default memo(PayComponentSalaryMassGrid);
